import type { DocPage } from "../types";

export const page: DocPage = {
  slug: "deployments",
  title: "Strategy deployments",
  navLabel: "Deployments",
  description:
    "Assign a tested Strategy version to a Portfolio Account, choose its capital and active window, and supervise its execution.",
  section: "strategies",
  published: "2026-08-03",
  updated: "2026-08-03",
  targets: [
    "deploy a Mavori strategy",
    "paper strategy deployment",
    "manage a strategy deployment",
  ],
  related: ["strategies", "backtesting", "accounts", "activity"],
  blocks: [
    {
      type: "lead",
      text: "A Strategy Deployment assigns one tested Strategy version to one Portfolio Account for a defined execution window. The Strategy, account, capital, permissions, and Risk Controls remain visible together so you can supervise what is running.",
    },

    { type: "h2", text: "Prepare the deployment" },
    {
      type: "p",
      text: "Open the Strategy's **Deployment** tab after reviewing Setup and the latest [backtest](/docs/backtesting). The selected version needs complete checks, usable data coverage, and a target instrument list. The configuration dialog shows the accounts and execution choices currently available to you.",
    },
    {
      type: "ul",
      items: [
        "Choose the exact Portfolio Account that will fund and receive the activity.",
        "Set the capital assigned to the deployment without exceeding the account's available amount.",
        "Choose the end date and, when useful, a future start date.",
        "Review the account's Risk Controls and any setup requirements shown before confirmation.",
      ],
    },

    { type: "h2", text: "Deploy to a paper account" },
    {
      type: "p",
      text: "A deployment uses an eligible Mavori Paper Account and simulated fills, making it the next environment for observing a Strategy after backtesting. The configuration dialog offers only destinations supported for the selected Strategy, account, and plan.",
    },
    {
      type: "callout",
      variant: "warning",
      title: "Deployment is a separate decision",
      text: "A completed or profitable backtest does not authorize execution. Confirm the version, account, capital, dates, and Risk Controls every time you deploy or redeploy.",
    },

    { type: "h2", text: "Monitor the running Strategy" },
    {
      type: "p",
      text: "The Deployment tab shows lifecycle status, assigned capital, dates, performance, and fills. Portfolio also shows deployments associated with the selected account. Use these views together with [Trade Logs and Journal](/docs/activity) to connect Strategy activity to account outcomes and recorded rationale.",
    },
    {
      type: "p",
      text: "A needs-attention or risk-stopped state should be reviewed before any restart. Read the stated reason, check the account and recent fills, then decide whether the Strategy, deployment settings, or Risk Controls need attention.",
    },

    { type: "h2", text: "Stop or revise a deployment" },
    {
      type: "p",
      text: "Use **Stop deployment** when the Strategy should no longer make new execution decisions for that account. Then review Trade Logs for activity already submitted or filled. To change the Strategy itself, stop the active deployment, fork or edit an eligible version, backtest it, and create a new supervised deployment decision.",
    },
  ],
};
